/**
 * apiConfig.js
 * Utility to dynamically determine API URLs based on the current domain
 * and provide an axios instance with proper configuration
 */
import axios from 'axios';

// The account context registration has been removed
// Components should now explicitly include accountId in their API calls

/**
 * Gets the base API URL dynamically based on the current domain
 * Falls back to the environment variable if needed
 */
export const getApiBaseUrl = () => {
  // Get the current hostname
  const hostname = window.location.hostname;

  // If we're in a development environment, use the environment variable
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL;
  }

  // For production, use the current domain with /backend path
  // This ensures each subdomain uses its own backend path
  return `https://${hostname}/backend`;
};

/**
 * Helper function to get a full API URL for a specific endpoint
 * @param {string} endpoint - The API endpoint (without leading slash)
 * @returns {string} The full API URL
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${formattedEndpoint}`;
};

/**
 * Create a configured axios instance that automatically uses the correct base URL
 */
export const api = axios.create();

// Add a request interceptor to dynamically set the base URL and include API key
api.interceptors.request.use((config) => {
  // Add API key to all requests
  config.headers = config.headers || {};

  // Access the API key from environment variable
  const apiKey = process.env.REACT_APP_API_KEY;

  config.headers['x-api-key'] = apiKey;

  // If the URL is already absolute (starts with http:// or https://), don't modify it
  if (config.url && (config.url.startsWith('http://') || config.url.startsWith('https://'))) {
    return config;
  }

  // For relative URLs, prepend the dynamic base URL
  const baseUrl = getApiBaseUrl();
  const url = config.url || '';
  config.url = `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;

  // Note: The accountId handling has been removed
  // Components should now explicitly include accountId in their API calls

  return config;
});

// The setupDynamicApiUrls function has been removed as all API calls now use getApiUrl() directly

/**
 * Helper function to log the current API configuration
 * Useful for debugging API URL issues
 */
export const logApiConfig = () => {
  // This function is kept for backward compatibility but no longer logs anything
  // Remove any calls to this function in your code
};

/**
 * IMPORTANT: Migration Guide
 *
 * Many components are using axios directly instead of this configured API instance.
 * This is causing API key authentication to fail because the API key is not being included in the headers.
 *
 * To fix this issue:
 * 1. Replace 'import axios from "axios";' with 'import { api } from "../utils/apiConfig";'
 * 2. Replace all instances of 'axios.get', 'axios.post', etc. with 'api.get', 'api.post', etc.
 *
 * Example:
 * Before: const response = await axios.get(getApiUrl('/api/endpoint'));
 * After:  const response = await api.get(getApiUrl('/api/endpoint'));
 */

export default {
  getApiBaseUrl,
  getApiUrl,
  api,
  logApiConfig
};
